import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import StockRequestService from '../services/StockRequestService';

const initialState = {
    datas: [],
    details: null,
    pagination: {
        page: 0,
        per_page: 10,
        total: 0,
        totalPages: 0,
    },
    error: null,
    loading: false,
    submitLoading: false,
};

export const fetchStockRequests = createAsyncThunk(
    'stockRequest/all',
    async (params) => {
        try {
            return await StockRequestService.All(params);
        } catch (e) {
            return e.response;
        }
    },
);

export const createStockRequest = createAsyncThunk(
    'stockRequest/create',
    async (data) => {
        try {
            return await StockRequestService.Create(data);
        } catch (error) {
            return error.response;
        }
    },
);

export const fetchStockRequestDetails = createAsyncThunk(
    'stockRequest/details',
    async (id) => {
        try {
            return await StockRequestService.Get(id);
        } catch (error) {
            return error.response;
        }
    },
);

export const updateStockRequest = createAsyncThunk(
    'stockRequest/update',
    async ({ id, data }) => {
        try {
            return await StockRequestService.Update(id, data);
        } catch (error) {
            return error.response;
        }
    },
);

export const deleteStockRequest = createAsyncThunk(
    'stockRequest/delete',
    async (id) => {
        try {
            return await StockRequestService.Delete(id);
        } catch (error) {
            return error.response;
        }
    },
);

export const submitStockRequest = createAsyncThunk(
    'stockRequest/submit',
    async (id) => {
        try {
            return await StockRequestService.Submit(id);
        } catch (error) {
            return error.response;
        }
    },
);

export const cancelStockRequest = createAsyncThunk(
    'stockRequest/cancel',
    async (id) => {
        try {
            return await StockRequestService.Cancel(id);
        } catch (error) {
            return error.response;
        }
    },
);

const stockRequestSlice = createSlice({
    name: 'stockRequest',
    initialState,
    reducers: {
        resetStockRequestDetails: (state) => {
            state.details = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchStockRequests.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchStockRequests.fulfilled, (state, action) => {
                if (action.payload?.status === 200) {
                    state.datas = action.payload?.data?.data?.stockRequests || [];
                    state.pagination = action.payload?.data?.data?.pagination || initialState.pagination;
                } else {
                    state.error = action.payload?.data?.message;
                    toast.error(state.error);
                }
                state.loading = false;
            })
            .addCase(fetchStockRequests.rejected, (state) => {
                state.loading = false;
            })
            .addCase(createStockRequest.pending, (state) => {
                state.submitLoading = true;
            })
            .addCase(createStockRequest.fulfilled, (state, action) => {
                if (action.payload?.status !== 200) {
                    toast.error(action.payload?.data?.message || 'Something went wrong');
                }
                state.submitLoading = false;
            })
            .addCase(createStockRequest.rejected, (state) => {
                state.submitLoading = false;
            })
            .addCase(fetchStockRequestDetails.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchStockRequestDetails.fulfilled, (state, action) => {
                if (action.payload?.status === 200) {
                    state.details = action.payload?.data?.data;
                } else {
                    toast.error(action.payload?.data?.message);
                }
                state.loading = false;
            })
            .addCase(updateStockRequest.pending, (state) => {
                state.submitLoading = true;
            })
            .addCase(updateStockRequest.fulfilled, (state, action) => {
                if (action.payload?.status !== 200) {
                    toast.error(action.payload?.data?.message || 'Something went wrong');
                }
                state.submitLoading = false;
            })
            .addCase(updateStockRequest.rejected, (state) => {
                state.submitLoading = false;
            })
            .addCase(submitStockRequest.pending, (state) => {
                state.submitLoading = true;
            })
            .addCase(submitStockRequest.fulfilled, (state, action) => {
                state.submitLoading = false;
                if (action.payload?.status === 200) {
                    state.details = action.payload?.data?.data;
                } else {
                    toast.error(action.payload?.data?.message || 'Something went wrong');
                }
            })
            .addCase(submitStockRequest.rejected, (state) => {
                state.submitLoading = false;
            })
            .addCase(cancelStockRequest.pending, (state) => {
                state.submitLoading = true;
            })
            .addCase(cancelStockRequest.fulfilled, (state, action) => {
                state.submitLoading = false;
                if (action.payload?.status === 200) {
                    state.details = action.payload?.data?.data;
                } else {
                    toast.error(action.payload?.data?.message || 'Something went wrong');
                }
            })
            .addCase(cancelStockRequest.rejected, (state) => {
                state.submitLoading = false;
            })
            .addCase(deleteStockRequest.fulfilled, (state, action) => {
                if (action.payload?.status === 200) {
                    toast.success(action.payload?.data?.message);
                } else {
                    toast.error(action.payload?.data?.message);
                }
            });
    },
});

export const { resetStockRequestDetails } = stockRequestSlice.actions;

export const stockRequestSelector = (state) => state?.stockRequest;

export default stockRequestSlice.reducer;
